import { calculateOfferCargoProcessStatus } from "@/features/offer-cargo-process-status";
import {
  type OFFER_CARGO_PROCESS_RECEPTION_STATUS,
  calculateOfferCargoProcessReceptionStatus,
} from "@/features/offer-cargo-process-status/badge-status";
import { MobileCargoList } from "@/pages/admin-cargo-list/ui/mobile/cargo-list";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { excludeNull } from "@/shared/lib/collection";
import { fromIso8601DateTime } from "@/shared/lib/datetime";
import { transformLoadable } from "@/shared/lib/loadable";
import { entries } from "@/shared/lib/object";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BasePagination } from "@/shared/ui/base-pagination";
import { BasePaginationControl } from "@/shared/ui/base-pagination-control";
import {
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "@tanstack/react-router";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import type { AdminCargoListPageProps } from "../model";
import { CargoTable } from "./CargoTable";
import { SearchFormCard } from "./SearchForms";
import { MobileSearchForms } from "./SearchForms/mobile";
import {
  OfferCargoSearchParamChipList,
  type OfferCargoSearchParamChipList_ParamKeys,
} from "./search-param-chip-list";
const TAG_01_COLOR = "#52A1D6";

export const AdminCargoListPageUi: React.FC<AdminCargoListPageProps> = (
  props,
) => {
  const links = [{ label: "荷物一覧" }];

  const [showFilters, setShowFilters] = useState(false);
  const [receptionStatusFilter, setReceptionStatusFilter] =
    useState<OFFER_CARGO_PROCESS_RECEPTION_STATUS>();
  const [companyNameFilter, setCompanyNameFilter] = useState("");

  const filteredParamCount = useMemo(() => {
    let count = 0;
    if (receptionStatusFilter) {
      count++;
    }
    if (companyNameFilter) {
      count++;
    }
    if (
      props.getDraftOffersCargosParams?.loadingOnFrom ||
      props.getDraftOffersCargosParams?.loadingOnTo
    ) {
      count++;
    }
    if (
      props.getDraftOffersCargosParams?.unloadingOnFrom ||
      props.getDraftOffersCargosParams?.unloadingOnTo
    ) {
      count++;
    }
    for (const [key, value] of entries(
      props.getDraftOffersCargosParams ?? {},
    )) {
      if (
        key === "loadingOnFrom" ||
        key === "loadingOnTo" ||
        key === "unloadingOnFrom" ||
        key === "unloadingOnTo"
      ) {
        continue;
      }
      if (value) {
        count++;
      }
    }
    return count;
  }, [
    props.getDraftOffersCargosParams,
    receptionStatusFilter,
    companyNameFilter,
  ]);
  const filteredDraftOfferCargos = useMemo<
    AdminCargoListPageProps["draftOfferCargos"]
  >(() => {
    if (props.draftOfferCargos.status !== "success") {
      return props.draftOfferCargos;
    }
    return {
      ...props.draftOfferCargos,
      data: {
        ...props.draftOfferCargos.data,
        data: props.draftOfferCargos.data.data.filter((draftOfferCargo) => {
          // TODO: バックエンドでフィルターできるよう処理を委嘱する
          // WORKAROUND: company_name での検索APIがないためUI側でフィルターする
          const offerCargoProcess = transformLoadable(
            draftOfferCargo.process,
            (process) => {
              return {
                draftOfferCargo: { ...draftOfferCargo, process },
                deliveryProposalOfferCargos:
                  process.deliveryProposalOfferCargos,
                contractOfferCargos: excludeNull([
                  process.contractProposalOfferCargo,
                  process.contractConcludedOfferCargo,
                ]),
              };
            },
          );
          const offerCargoProcessStatus = transformLoadable(
            offerCargoProcess,
            (process) => {
              return calculateOfferCargoProcessStatus({
                process,
              });
            },
          );
          if (offerCargoProcessStatus.status !== "success") {
            return false;
          }
          const receptionBadgeStatus =
            calculateOfferCargoProcessReceptionStatus({
              viewer: "shipper",
              status: offerCargoProcessStatus.data,
            });
          const matchReceptionStatus = (() => {
            if (!receptionStatusFilter) {
              return true;
            }
            return receptionBadgeStatus === receptionStatusFilter;
          })();
          const matchCompanyName = (() => {
            if (!companyNameFilter) {
              return true;
            }
            return (
              draftOfferCargo.company?.name?.includes(companyNameFilter) ??
              false
            );
          })();
          return matchReceptionStatus && matchCompanyName;
        }),
      },
    };
  }, [props.draftOfferCargos, receptionStatusFilter, companyNameFilter]);

  const pagination = filteredDraftOfferCargos.data?.pagination;

  const onDeleteChip = useCallback(
    (key: OfferCargoSearchParamChipList_ParamKeys) => {
      if (key === "receptionStatus") {
        setReceptionStatusFilter(undefined);
        return;
      }
      if (key === "companyName") {
        setCompanyNameFilter("");
      }
      if (props.getDraftOffersCargosParams) {
        if (key === "loadingOn") {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            loadingOnFrom: undefined,
            loadingOnTo: undefined,
          });
        } else if (key === "unloadingOn") {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            unloadingOnFrom: undefined,
            unloadingOnTo: undefined,
          });
        } else {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            [key]: undefined,
          });
        }
      }
    },
    [
      props.getDraftOffersCargosParams,
      props.onGetDraftOffersCargosParamsChange,
    ],
  );

  return (
    <Stack px={{ xs: 2, sm: 4 }} pb={2} spacing={{ xs: 3, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          荷物一覧
        </Typography>
        <BaseButton
          component={Link}
          variant="contained"
          sx={{
            fontSize: "16px",
          }}
          startIcon={<BaseIcon name="plus" htmlColor="white" />}
          onClick={props.onCreateOfferCargoClick}
        >
          荷物を新規登録する
        </BaseButton>
      </Stack>
      <Box
        boxShadow="0px 1px 10px 0px #0000001A"
        display="flex"
        justifyContent="center"
        alignItems="center"
        maxWidth="120px"
        p={2}
        borderRadius={4}
      >
        <Box textAlign="center">
          <Typography variant="body1">受付中</Typography>
          <Typography
            variant="body1"
            color={TAG_01_COLOR}
            fontSize="30px"
            fontWeight="700"
          >
            {props.draftOfferCargos.status === "success" ? (
              props.draftOfferCargos.data.data.filter(
                (cargo) => fromIso8601DateTime(cargo.deadlineAt) > new Date(),
              ).length
            ) : props.draftOfferCargos.status === "error" ? (
              "?"
            ) : (
              <CircularProgress size={24} />
            )}
          </Typography>
        </Box>
      </Box>
      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between">
          <BaseButton
            sx={{
              paddingRight: 1,
              borderColor: "grey.300",
              color: "grey.700",
            }}
            variant="outlined"
            startIcon={<BaseIcon name="filter" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Typography fontWeight={700} mr={1}>
              フィルター
            </Typography>
            <Box
              width={"24px"}
              height={"24px"}
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={0}
              borderRadius="50%"
              bgcolor="primary.500"
              color="white"
            >
              <Typography>{filteredParamCount}</Typography>
            </Box>
          </BaseButton>
        </Box>

        {/* PC用の検索フォーム、チップ */}
        <Box display={{ xs: "none", sm: "block" }}>
          <Collapse in={showFilters}>
            <SearchFormCard
              getDraftOffersCargosParams={props.getDraftOffersCargosParams}
              companies={props.companies}
              companyNameFilter={companyNameFilter}
              receptionStatusFilter={receptionStatusFilter}
              onGetOffersCargosParamsChange={
                props.onGetDraftOffersCargosParamsChange
              }
              onGetOffersCargosParamsReset={
                props.onGetDraftOffersCargosParamsReset
              }
              onCompanyNameFilterChange={setCompanyNameFilter}
              onReceptionStatusFilterChange={setReceptionStatusFilter}
            />
          </Collapse>
          <Collapse in={!showFilters}>
            <OfferCargoSearchParamChipList
              getDraftOffersCargosParams={props.getDraftOffersCargosParams}
              receptionStatusFilter={receptionStatusFilter}
              companyNameFilter={companyNameFilter}
              onDelete={onDeleteChip}
            />
          </Collapse>
        </Box>

        {/* SP用の検索フォーム、チップ */}
        <Box display={{ xs: "block", sm: "none" }}>
          <MobileSearchForms
            open={showFilters}
            onClose={() => setShowFilters(false)}
            getDraftOffersCargosParams={props.getDraftOffersCargosParams}
            companies={props.companies}
            companyNameFilter={companyNameFilter}
            receptionStatusFilter={receptionStatusFilter}
            onGetOffersCargosParamsChange={
              props.onGetDraftOffersCargosParamsChange
            }
            onGetOffersCargosParamsReset={
              props.onGetDraftOffersCargosParamsReset
            }
            onCompanyNameFilterChange={setCompanyNameFilter}
            onReceptionStatusFilterChange={setReceptionStatusFilter}
          />

          <OfferCargoSearchParamChipList
            getDraftOffersCargosParams={props.getDraftOffersCargosParams}
            receptionStatusFilter={receptionStatusFilter}
            companyNameFilter={companyNameFilter}
            onDelete={onDeleteChip}
          />
        </Box>
      </Stack>
      {filteredDraftOfferCargos.status === "success" ? (
        <Stack direction="column" spacing={2}>
          {pagination && (
            <BasePaginationControl
              page={pagination.page}
              perPage={pagination.perPage}
              totalCount={pagination.totalCount}
              onPerPageChange={pagination.onPerPageChange}
            />
          )}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <CargoTable
              draftOfferCargos={filteredDraftOfferCargos.data}
              onSelectOfferCargo={props.onSelectOfferCargo}
            />
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <MobileCargoList
              draftOfferCargos={filteredDraftOfferCargos.data.data}
            />
          </Box>
          {pagination && (
            <BasePagination
              page={pagination.page}
              perPage={pagination.perPage}
              totalCount={pagination.totalCount}
              onPageChange={pagination.onPageChange}
            />
          )}
        </Stack>
      ) : filteredDraftOfferCargos.status === "loading" ? (
        <Stack justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <Typography color="error">エラーが発生しました</Typography>
      )}
    </Stack>
  );
};
